
import textNode from './node_text.js';
import numberNode from './node_number.js';
import chooseNode from './node_choose.js';
import dropdownNode from './node_dropdown.js';
import paragraphNode from './node_paragraph.js';
import errorNode from './node_error.js';
import calculatorNode from './node_calculator.js';

function randomString() {
	return Math.random().toString().substr(2, 10);
}

function buildForm(spec) {

	// Define recursive function to construct definition from nested Item Sets
	// and items, including any conditional branches / logic.

	let itemIndices = {};
	function getIdx(item) {
		if (Object.keys(itemIndices).includes(item.id)) {
			itemIndices[item.id] = itemIndices[item.id] + 1;
		} else {
			itemIndices[item.id] = 0;
		}
		return itemIndices[item.id];
	}

	function makeNode(item) {
		let idx = getIdx(item);
		switch (item.type) {
			case "choose-1":
			case "choose-n":
				return chooseNode(item, idx);
				break;
			case "dropdown-1":
				return dropdownNode(item, idx);
				break;
			case "text":
			case "text-area":
				return textNode(item, idx);
				break;
			case "number":
				return numberNode(item, idx);
				break;
			case "paragraph":
				return paragraphNode(item, idx);
				break;
			case "error":
				return errorNode(item, idx);
				break;
			case "calculator":
				return calculatorNode(item, idx);
			default:
				return { id: randomString() };
				break;
		}
	}

	function makeConditions(logic) {

		let conditions = [];

		for (let c of logic.conditions) {

			let item = spec.Items.filter(d => d.id == c.item)[0];

			switch (item.type) {
				case "choose-1":
					conditions.push({
						id: randomString(),
						block: {
							choice: `${item.id}-${itemIndices[item.id]}-choice-${c.value}`,
							type: "@tripetto/block-multiple-choice",
							version: "7.0.1",
							node: `${item.id}-${itemIndices[item.id]}-node`,
							slot: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0')
						}
					});
					break;
				case "dropdown-1":
					conditions.push({
						id: randomString(),
						block: {
							option: `${item.id}-${itemIndices[item.id]}-choice-${c.value}`,
							type: "@tripetto/block-dropdown",
							version: "7.0.0",
							node: `${item.id}-${itemIndices[item.id]}-node`,
							slot: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0')
						}
					});
					break;
				case "text":
					if (["invertregex", "regex"].includes(c.condition)){
						conditions.push({
						id: randomString(),
						block: {
							type: "@tripetto/block-regex",
							version: "4.0.2",
							slot: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0'),
							variable: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0'),
							regex: c.value,
							invert: c.condition == "invertregex" ? true : false
						}
						});
					} else if (["equal","not-equal"].includes(c.condition)){
						conditions.push({
						id: randomString(),
						block: {
							type: "@tripetto/block-evaluate",
							version: "4.0.2",
							slot: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0'),
							variable: `${item.id}${itemIndices[item.id]}`.padEnd(64, '0'),
							mode: c.condition,
							value: `${c.value}${itemIndices[c.value]}`.padEnd(64, '0'),
							ignoreCase: false
						}
						});
					}
					break;
				default:
					break;
			}

		}


		return conditions;
	}

	let maxRecursiveCalls = 200,
		nRecursiveCalls = 0;

	function makeCluster({
			item = null,
			elements = null,
			logic = {conditions: []},
			required = null
		}) {

		nRecursiveCalls++;

		let hasItem = (item !== null),
			hasElements = (elements !== null),
			hasLogic = (logic.conditions !== undefined ? logic.conditions.length > 0 : false);

		let cluster = {
			"id": randomString()
		};

		if (hasItem & !hasLogic) {
			
			// Always use Item required status if available, Item Set required status only used as backup.
			item.required = item.hasOwnProperty('required') ? item.required : (required == true);
			cluster.nodes = item.type == "calculator" ? makeNode(item) : [makeNode(item)];

		} else if (hasItem & hasLogic) {

			// Always use Item required status if available, Item Set required status only used as backup.
			item.required = item.hasOwnProperty('required') ? item.required : (required == true);
			cluster.nodes = [{ id: randomString() }];
			cluster.branches = [{
				id: randomString(),
				clusters: [makeCluster({ item: item })],
				conditions: makeConditions(logic),
				culling: logic.conditionAggregator
			}]

		} else if (hasElements & !hasLogic) {

			cluster.nodes = [{ id: randomString() }];
			cluster.branches = [{
				id: randomString(),
				clusters: makeClusters(elements)
			}]

		} else if (hasElements & hasLogic) {
		
			cluster.nodes = [{ id: randomString() }];
			cluster.branches = [{
				id: randomString(),
				clusters: makeClusters(elements),
				conditions: makeConditions(logic),
				culling: logic.conditionAggregator
			}]

		}

		return cluster;
	}

	function makeClusters(seq, isMain = false) {

		nRecursiveCalls++;

		let clusters = [];

		for (let el of seq) {

			// Handle differently if an Item, or a link to an Item or ItemSet.
			if (el.isItem) {

				clusters.push(makeCluster({ item: el }));

			} else if (el.collection == "Items") {

				let logic = {
					conditionAggregator: el.conditionAggregator,
					conditions: el.conditions
				};

				// Push Item details to items.
				let matchingItems = spec.Items.filter(d => d.id == el.element);
				if (matchingItems.length > 0) {
					clusters.push(makeCluster({
						item: matchingItems[0],
						logic: logic,
						required: el.required
					}));
				}

			} else if (el.collection == "ItemSets") {

				let logic = {
					conditionAggregator: el.conditionAggregator,
					conditions: el.conditions
				};

				// Extract elements in ItemSet and recursively call makeClusters on it.
				let matchingItemSets = spec.ItemSets.filter(d => d.id == el.element);
				if (matchingItemSets.length > 0 && nRecursiveCalls < maxRecursiveCalls) {
					
					clusters.push(makeCluster({
						elements: matchingItemSets[0].elements,
						logic: logic
					}));
					
				} else if (nRecursiveCalls >= maxRecursiveCalls) {
					console.log("Maximum recursive calls exceeded.")
				}
			}
		}

		if(isMain && spec.ItemSet.hasOwnProperty("endscreenBranches") && (spec.ItemSet.endscreenBranches.length > 0)){
			clusters.push(makeEndScreen(spec.ItemSet));
		}


		return clusters;
	}

	// Create top-level sequence of clusters.
	// (to handle both individual Items and Item Sets)

	let seq = spec.ItemSet.elements;

	// Construct definition.

	let def = {
		clusters: makeClusters(seq, true),
		builder: {
			name: "tripetto",
			version: "6.2.1"
		},
		epilogue: {
			title: "Your voice matters!",
			description: "Thank you for completing the survey."
		}
	};

	function makeEndScreenBranches(brs){
		return brs.map(br => {
			let branch = {
				"id": randomString(),
				conditions: [],
				"epilogue": {
					"title": "Your voice matters!",
					"description": "Thank you for completing the survey.",
					"redirectUrl":`${window.location.origin}/?p=EndScreen&s=${br.endScreen}`
				},
				"jump": "abort",
			}
			if (br.hasOwnProperty("conditions")){
				branch.conditions =
					br.conditions.map(c => {
						return {
							"id": randomString(),
							"block": {
								"option": `${c.item}-0-choice-${c.value}`,
								"type": "@tripetto/block-dropdown",
                        		"version": "7.0.0",
								"node": `${c.item}-0-node`,
								"slot": `${c.item}0`.padEnd(64, '0'),
							},
						}				
				});
			}

			return branch;
		});
	}

	function makeEndScreenConditions(cons){
		return cons.map(con => {
			let matchingItem = spec.Items.filter(d => d.id == con.item);
			if(matchingItem.length > 0){
				matchingItem = matchingItem[0]
			}

			if(matchingItem.type === "calculator"){
				return {
					"id": randomString(),
					"block": {
						"type": "@tripetto/block-calculator",
						"version": "4.0.6",
						"node": `${con.item}-0-node`,
						"slot": `${con.item}0`.padEnd(64, '0'),
						"mode": con.mode,
						"to": con.to,
						"value": con.value
					}
				}
			} else {
				return {
					"id": randomString(),
					"block": {
						type: "@tripetto/block-multiple-choice",
						version: "7.0.1",
						"node": `${con.item}-0-node`,
						"slot": `${con.item}0`.padEnd(64, '0'),
						"choice": `${con.item}-0-choice-${con.value}`,
					}
				}
			}
			});
	};

	function makeEndScreen(is){
		let endscreen = {
			"id": randomString(),
			"name": "Final Screen",
			"nodes": [
				{
					"id": randomString(),
					"name": "",
					"nameVisible": true,
				}
			]
		}

		endscreen.branches = [
			{
				"id": randomString(),
				"clusters": [{
					"id": randomString(),
					"nodes": [
						{
							"id": randomString(),
							"name": "",
							"nameVisible": false,
						}
					],
					"branches": makeEndScreenBranches(is.endscreenBranches),
				}],
				"conditions": makeEndScreenConditions(is.endscreenConditions || []),
				...(is.culling ? is.culling : null)
			}
		]

		return endscreen;
	}

	if (spec.hasOwnProperty('Survey')) {
		let redirectUrl = `${window.location.origin}/?p=EndScreen&s=${spec.Survey.endScreen}`;
		def.epilogue.redirectUrl = redirectUrl;
	}

	return def;
}

export default buildForm;